// Web Audio API sound generator — no external audio files needed.
// Produces woodchuck squeaks, arcade jingles, and game-over tones.

let ctx = null;

function getCtx() {
  if (!ctx) {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return ctx;
}

function playTone(freq, duration, type = "sine", volume = 0.3, rampTo = null) {
  const c = getCtx();
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, c.currentTime);
  if (rampTo) {
    osc.frequency.exponentialRampToValueAtTime(
      rampTo,
      c.currentTime + duration,
    );
  }
  gain.gain.setValueAtTime(volume, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + duration);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start(c.currentTime);
  osc.stop(c.currentTime + duration);
}

const SoundManager = {
  // Short chirpy squeak — woodchuck tap sound
  playSqueak() {
    const c = getCtx();
    const now = c.currentTime;

    // Main chirp
    const osc = c.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.exponentialRampToValueAtTime(1400, now + 0.04);
    osc.frequency.exponentialRampToValueAtTime(800, now + 0.08);
    osc.frequency.exponentialRampToValueAtTime(400, now + 0.12);

    const gain = c.createGain();
    gain.gain.setValueAtTime(0.25, now);
    gain.gain.linearRampToValueAtTime(0.3, now + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.13);

    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(now);
    osc.stop(now + 0.14);
  },

  // Ascending major arpeggio — level complete jingle
  playLevelComplete() {
    const notes = [523, 659, 784, 1047, 1319]; // C5-E5-G5-C6-E6
    notes.forEach((freq, i) => {
      setTimeout(() => playTone(freq, 0.2, "square", 0.15), i * 120);
    });
    // Final sustained chord
    setTimeout(() => {
      playTone(1047, 0.5, "sine", 0.1);
      playTone(1319, 0.5, "sine", 0.08);
      playTone(1568, 0.5, "sine", 0.06);
    }, 600);
  },

  // Descending sad tone — game over
  playGameOver() {
    const c = getCtx();
    const now = c.currentTime;

    const osc = c.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(400, now);
    osc.frequency.exponentialRampToValueAtTime(80, now + 1.0);

    const gain = c.createGain();
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 1.2);

    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(now);
    osc.stop(now + 1.3);
  },

  // Quick "speed warning" alert beep
  playSpeedWarning() {
    playTone(200, 0.1, "square", 0.12);
    setTimeout(() => playTone(150, 0.15, "square", 0.1), 120);
  },

  // Combo milestone ascending arpeggio — higher notes for higher milestones
  playComboMilestone(milestone) {
    const baseFreqs = {
      10: [392, 494, 587], // G4-B4-D5 (green tier)
      25: [523, 659, 784], // C5-E5-G5 (cyan tier)
      50: [659, 784, 988], // E5-G5-B5 (magenta tier)
      100: [784, 988, 1175, 1319], // G5-B5-D6-E6 (gold tier)
      200: [1047, 1319, 1568, 1760], // C6-E6-G6-A6 (rainbow tier)
      500: [1319, 1568, 1760, 2093], // E6-G6-A6-C7 (legendary)
    };
    const freqs = baseFreqs[milestone] || baseFreqs[10];
    freqs.forEach((freq, i) => {
      setTimeout(() => playTone(freq, 0.15, "sine", 0.12), i * 80);
    });
  },

  // Pattern chain completion — triumphant jingle
  playPatternComplete() {
    const notes = [659, 784, 988, 1319]; // E5-G5-B5-E6
    notes.forEach((freq, i) => {
      setTimeout(() => playTone(freq, 0.12, "square", 0.1), i * 60);
    });
    setTimeout(() => {
      playTone(1319, 0.3, "sine", 0.08);
      playTone(988, 0.3, "sine", 0.06);
    }, 280);
  },

  // Hazard tile warning — sharp alert beep
  playHazardWarning() {
    playTone(880, 0.06, "square", 0.15);
    setTimeout(() => playTone(880, 0.06, "square", 0.15), 80);
  },

  // Coin-drop sound — INSERT COIN arcade feel
  playCoinDrop() {
    const c = getCtx();
    const now = c.currentTime;

    // Metallic ping rising
    const osc1 = c.createOscillator();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(2000, now);
    osc1.frequency.exponentialRampToValueAtTime(4000, now + 0.08);
    osc1.frequency.exponentialRampToValueAtTime(3000, now + 0.2);

    const gain1 = c.createGain();
    gain1.gain.setValueAtTime(0.2, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    osc1.connect(gain1);
    gain1.connect(c.destination);
    osc1.start(now);
    osc1.stop(now + 0.25);

    // Coin-slot clunk
    setTimeout(() => {
      playTone(150, 0.05, "square", 0.2);
      setTimeout(() => playTone(300, 0.08, "sine", 0.15), 40);
    }, 100);
  },

  // Boss level fanfare — dramatic intro
  playBossFanfare() {
    const notes = [392, 392, 523, 659, 784]; // G4-G4-C5-E5-G5
    const durations = [0.1, 0.1, 0.1, 0.1, 0.4];
    let offset = 0;
    notes.forEach((freq, i) => {
      setTimeout(() => playTone(freq, durations[i], "square", 0.15), offset);
      offset += durations[i] * 1000 + 30;
    });
  },

  // Rhythm beat metronome tick
  playBeatCue() {
    const c = getCtx();
    const now = c.currentTime;

    const osc = c.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(1200, now);

    const gain = c.createGain();
    gain.gain.setValueAtTime(0.08, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(now);
    osc.stop(now + 0.06);
  },

  // Decay storm warning — rising alarm
  playStormWarning() {
    const c = getCtx();
    const now = c.currentTime;

    const osc = c.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(200, now);
    osc.frequency.exponentialRampToValueAtTime(800, now + 0.3);

    const gain = c.createGain();
    gain.gain.setValueAtTime(0.1, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(now);
    osc.stop(now + 0.35);
  },

  // Low momentum heartbeat pulse
  playHeartbeat() {
    playTone(60, 0.15, "sine", 0.2);
    setTimeout(() => playTone(50, 0.2, "sine", 0.15), 180);
  },

  // Resume audio context (needed after user gesture on some browsers)
  resume() {
    if (ctx && ctx.state === "suspended") {
      ctx.resume();
    }
  },
};

export default SoundManager;
